// src/authpages/timetables/MicrosoftCalendarConnect.js
import React, { useState, useEffect } from 'react';
import { Button, Alert } from 'react-bootstrap';

// Import utilities
import { 
  loadMsalLibrary, 
  initMsalForImplicitFlow, 
  loginWithMicrosoft, 
  getAccessToken, 
  fetchMicrosoftCalendarEvents 
} from './MicrosoftCalendarUtils';

const MicrosoftCalendarConnect = ({ onCalendarEvents }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  const [msalInstance, setMsalInstance] = useState(null);
  const [lastSynced, setLastSynced] = useState(null);
  const [events, setEvents] = useState([]);
  const [userInfo, setUserInfo] = useState(null);

  // Initialize on component mount
  useEffect(() => {
    const initMsal = async () => {
      try {
        // Load authentication tools
        const msalLoaded = await loadMsalLibrary();
        if (!msalLoaded) {
          // This shouldn't happen with our mock version
          setError('Failed to initialize Microsoft authentication tools');
          setIsLoading(false);
          return;
        }
        
        // Initialize with mock tools
        const msalApp = initMsalForImplicitFlow();
        setMsalInstance(msalApp);
        setIsLoading(false);
      } catch (err) {
        console.error('Initialization error:', err);
        setError('Failed to initialize: ' + err.message);
        setIsLoading(false);
      }
    };
    
    initMsal();
  }, []);

  // Function to sync calendar events
  const syncCalendar = async () => {
    try {
      setIsLoading(true);
      
      // Get calendar data
      const calendarEvents = await fetchMicrosoftCalendarEvents();
      
      // Update state
      setEvents(calendarEvents);
      const now = new Date();
      setLastSynced(now);
      
      // Notify parent component
      if (onCalendarEvents) {
        onCalendarEvents(calendarEvents, 'Microsoft Calendar');
      }
      
      setIsLoading(false);
      return calendarEvents;
    } catch (error) {
      console.error('Calendar sync error:', error);
      setError('Failed to sync calendar data');
      setIsLoading(false);
      throw error;
    }
  };

  // Connect to Microsoft Calendar
  const handleConnect = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Login with mock function
      const loginResponse = await loginWithMicrosoft();
      setUserInfo(loginResponse.account);
      setIsConnected(true);
      
      // Fetch calendar events
      await syncCalendar();
    } catch (err) {
      console.error('Connection error:', err);
      setError('Failed to connect: ' + err.message);
      setIsLoading(false);
    }
  };

  // Disconnect from Microsoft Calendar
  const handleDisconnect = async () => {
    setIsLoading(true);
    
    try {
      // Clear state
      setIsConnected(false);
      setUserInfo(null);
      setEvents([]);
      
      // Clear events in parent component
      if (onCalendarEvents) {
        onCalendarEvents([], 'Microsoft Calendar');
      }
      
      setIsLoading(false);
    } catch (err) {
      console.error('Disconnect error:', err);
      setError('Failed to disconnect: ' + err.message);
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="text-center">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div>
      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}
      
      {isConnected ? (
        <div>
          <Alert variant="success" className="mb-3">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <strong>Microsoft Calendar Connected</strong>
                {userInfo && (
                  <div className="small mt-1">
                    {userInfo.name || userInfo.username}
                  </div>
                )}
              </div>
            </div>
          </Alert>
          
          {events.length > 0 && (
            <p className="mb-3">
              <strong>{events.length}</strong> events imported
              {lastSynced && (
                <span className="text-muted ms-2">
                  (Last synced: {lastSynced.toLocaleString()})
                </span>
              )}
            </p>
          )}
          
          <div className="d-flex justify-content-between">
            <Button
              variant="outline-primary"
              onClick={syncCalendar}
              disabled={isLoading}
              className="me-2"
            >
              Sync Now
            </Button>
            
            <Button
              variant="outline-danger"
              onClick={handleDisconnect}
              disabled={isLoading}
            >
              Disconnect
            </Button>
          </div>
        </div>
      ) : (
        <div className="text-center">
          <Button
            variant="primary"
            className="w-100 py-2"
            onClick={handleConnect}
            disabled={isLoading}
          >
            Connect Microsoft Calendar
          </Button>
        </div>
      )}
    </div>
  );
};

export default MicrosoftCalendarConnect;